import { useEffect, useState } from "react";
import { X, ArrowUpRight, TrendingUp, TrendingDown, Target, Scale, Calendar, ExternalLink } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { api } from "../lib/api";
import { fmtMoney, fmtPct, displayTicker, marginColor } from "../lib/format";

const SIGNAL_THEME = {
  BUY:        { color: "text-moss-light",     bg: "bg-moss",                     accent: "moss" },
  ACCUMULATE: { color: "text-moss-light",     bg: "bg-moss-bg border-moss",      accent: "moss" },
  HOLD:       { color: "text-stone_brand-dark", bg: "bg-bone-100",               accent: "stone" },
  SELL:       { color: "text-terracotta",     bg: "bg-terracotta",               accent: "terracotta" },
  NO_DATA:    { color: "text-stone_brand",    bg: "bg-bone-100",                 accent: "stone" },
};

export default function SignalDetailModal({ holding, onClose }) {
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const cur = holding?.currency || "INR";
  const rec = holding?.recommendation || {};
  const theme = SIGNAL_THEME[rec.signal] || SIGNAL_THEME.NO_DATA;

  useEffect(() => {
    if (!holding?.ticker) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/stocks/${encodeURIComponent(holding.ticker)}/history?period=1y`);
        if (!cancelled) {
          const points = (data?.data || []).map((d) => ({
            date: d.date,
            price: d.close,
          }));
          setHistory(points);
        }
      } catch {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [holding?.ticker]);

  if (!holding) return null;

  const price = holding.current_price;
  const fv = holding.user_morningstar_fv;
  const t2030 = holding.user_target_2030;
  const avg = holding.avg_buy_price;
  const fvDiff = rec.fv_diff_pct;
  const upside2030 = rec.upside_2030_pct;

  return (
    <div
      className="fixed inset-0 z-[100] bg-stone_brand-dark/70 backdrop-blur-sm flex items-start justify-end p-0"
      onClick={onClose}
      data-testid="signal-detail-modal"
    >
      <div
        className="bg-bone-50 border-l border-line w-full sm:max-w-xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-start justify-between z-10">
          <div className="min-w-0">
            <div className="overline text-stone_brand-light mb-1">— Signal Detail</div>
            <div className="font-serif-display text-2xl text-stone_brand-dark font-medium leading-tight truncate">
              {holding.company_name || displayTicker(holding.ticker)}
            </div>
            <div className="font-mono text-sm text-stone_brand mt-0.5">{displayTicker(holding.ticker)}</div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-stone_brand hover:text-stone_brand-dark hover:bg-bone-100 rounded-sm transition-colors flex-shrink-0"
            data-testid="signal-detail-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Big Signal Banner */}
          <div className={`border rounded-sm p-5 text-bone-50 ${theme.bg} ${rec.signal === "ACCUMULATE" ? "text-moss-light border-moss-light/50" : rec.signal === "HOLD" ? "text-stone_brand-dark border-line" : ""}`}>
            <div className="font-mono text-[0.65rem] uppercase tracking-[0.25em] opacity-80 mb-1">
              Verdict
            </div>
            <div className="font-serif-display text-3xl font-semibold tracking-tight">
              {rec.label || "—"}
            </div>
            <div className="text-sm opacity-90 mt-2 leading-relaxed">{rec.reason || ""}</div>
          </div>

          {/* Price waterfall */}
          <div>
            <div className="overline mb-3">— The math</div>
            <div className="bg-white border border-line rounded-sm divide-y divide-line">
              <PriceRow
                icon={Calendar}
                label="Avg Buy Price"
                value={fmtMoney(avg, cur)}
                accent="text-stone_brand"
              />
              <PriceRow
                icon={TrendingUp}
                label="Current Live Price"
                value={fmtMoney(price, cur)}
                accent="text-stone_brand-dark font-semibold"
                badge={holding.pnl_pct !== null && holding.pnl_pct !== undefined ? {
                  text: fmtPct(holding.pnl_pct),
                  className: marginColor(holding.pnl_pct),
                } : null}
              />
              <PriceRow
                icon={Scale}
                label="Morningstar Fair Value"
                value={fv ? fmtMoney(fv, cur) : "Not set"}
                accent={fv ? "text-stone_brand-dark" : "text-stone_brand-light italic"}
                badge={fv && fvDiff !== null && fvDiff !== undefined ? {
                  text: fmtPct(fvDiff),
                  className: marginColor(fvDiff),
                } : null}
                hint={fv ? "Distance from current to fair value" : "Add a Morningstar FV from the Scanner page"}
              />
              <PriceRow
                icon={Target}
                label="Your 2030 Target"
                value={t2030 ? fmtMoney(t2030, cur, 0) : "Not set"}
                accent={t2030 ? "text-stone_brand-dark" : "text-stone_brand-light italic"}
                badge={t2030 && upside2030 !== null && upside2030 !== undefined ? {
                  text: fmtPct(upside2030),
                  className: marginColor(upside2030),
                } : null}
                hint={
                  t2030
                    ? `Upside from current price to your 2030 target${holding.user_multiplier_2030 ? ` (${holding.user_multiplier_2030})` : ""}`
                    : "No 2030 target on file for this ticker"
                }
              />
            </div>
          </div>

          {/* Sparkline */}
          <div>
            <div className="overline mb-3 flex items-center justify-between">
              <span>— 1-year price (with reference lines)</span>
              {!loadingHistory && history && history.length > 0 && (
                <span className="text-[0.6rem] font-mono normal-case tracking-normal text-stone_brand-light">
                  {history.length} pts
                </span>
              )}
            </div>
            <div className="bg-white border border-line rounded-sm p-4 h-56">
              {loadingHistory ? (
                <div className="h-full flex items-center justify-center text-stone_brand-light text-xs font-mono uppercase tracking-widest">
                  Loading history…
                </div>
              ) : !history || history.length === 0 ? (
                <div className="h-full flex items-center justify-center text-stone_brand-light text-xs font-mono uppercase tracking-widest">
                  No history available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={history} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "#7B8478", fontFamily: "JetBrains Mono" }}
                      tickFormatter={(d) => (d ? d.slice(0, 7) : "")}
                      tickLine={false}
                      axisLine={{ stroke: "#E5E1D8" }}
                      minTickGap={48}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#7B8478", fontFamily: "JetBrains Mono" }}
                      tickLine={false}
                      axisLine={{ stroke: "#E5E1D8" }}
                      width={56}
                      domain={["dataMin", "dataMax"]}
                      tickFormatter={(v) =>
                        v >= 10000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toLocaleString()
                      }
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#FFFFFF",
                        border: "1px solid #E5E1D8",
                        borderRadius: 2,
                        fontFamily: "JetBrains Mono",
                        fontSize: 11,
                      }}
                      labelStyle={{ color: "#5C6B64" }}
                      formatter={(v) => [fmtMoney(v, cur), "Price"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke="#4E6E5D"
                      strokeWidth={1.5}
                      dot={false}
                      isAnimationActive={false}
                    />
                    {avg && (
                      <ReferenceLine
                        y={avg}
                        stroke="#C58A6B"
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        label={{ value: "Avg buy", position: "insideTopLeft", fill: "#C58A6B", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      />
                    )}
                    {fv && (
                      <ReferenceLine
                        y={fv}
                        stroke="#4347B5"
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        label={{ value: "M-Star FV", position: "insideBottomLeft", fill: "#4347B5", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      />
                    )}
                    {t2030 && (
                      <ReferenceLine
                        y={t2030}
                        stroke="#7C8E73"
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        label={{ value: "2030 target", position: "insideTopRight", fill: "#5C6B64", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Rules used */}
          <div>
            <div className="overline mb-3">— How signals are computed</div>
            <div className="bg-white border border-line rounded-sm p-4 space-y-1.5">
              <RuleRow
                emoji="🟢"
                label="BUY"
                rule="2030 upside ≥ +100% AND current ≤ Morningstar FV"
                active={rec.signal === "BUY"}
              />
              <RuleRow
                emoji="🟡"
                label="ACCUMULATE"
                rule="2030 upside ≥ +50% AND current ≤ Morningstar FV × 1.10"
                active={rec.signal === "ACCUMULATE"}
              />
              <RuleRow
                emoji="⚪"
                label="HOLD"
                rule="Within fair-value band — no strong action"
                active={rec.signal === "HOLD"}
              />
              <RuleRow
                emoji="🔴"
                label="SELL"
                rule="Current ≥ 2030 target OR current ≥ Morningstar FV × 1.50"
                active={rec.signal === "SELL"}
              />
            </div>
          </div>

          {/* CTA */}
          <a
            href={`/stock/${encodeURIComponent(holding.ticker)}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-moss hover:text-moss-hover underline underline-offset-4 decoration-line"
            data-testid="signal-detail-open-full"
          >
            Open full research view <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}

function PriceRow({ icon: Icon, label, value, accent, badge, hint }) {
  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <Icon className="w-4 h-4 text-stone_brand-light mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="overline text-[0.6rem] mb-0.5">{label}</div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className={`font-mono text-base ${accent}`}>{value}</div>
          {badge && (
            <span className={`font-mono text-xs ${badge.className}`}>{badge.text}</span>
          )}
        </div>
        {hint && <div className="text-[0.65rem] text-stone_brand-light mt-1 leading-relaxed">{hint}</div>}
      </div>
    </div>
  );
}

function RuleRow({ emoji, label, rule, active }) {
  return (
    <div
      className={`flex items-start gap-2 text-xs leading-relaxed py-1 rounded-sm transition-colors ${
        active ? "text-stone_brand-dark font-semibold" : "text-stone_brand"
      }`}
    >
      <span className="flex-shrink-0">{emoji}</span>
      <span className={`font-mono ${active ? "" : "opacity-60"}`}>{label}</span>
      <span className={`flex-1 ${active ? "" : "opacity-70"}`}>— {rule}</span>
      {active && <ArrowUpRight className="w-3 h-3 text-moss mt-0.5 flex-shrink-0" />}
    </div>
  );
}
